import React, { useState, useMemo } from "react";
import { useDestinationAnalytics } from "../../hooks/use-destinations";
import type { DestinationAnalyticsProps } from "../../types/destination-types";

export function DestinationAnalytics({
  destinationId,
  dateRange,
  showDetails = true,
  compact = false,
}: DestinationAnalyticsProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<
    "7d" | "30d" | "90d" | "custom"
  >("30d");

  // Calculate date range based on selected period
  const calculatedDateRange = useMemo(() => {
    if (dateRange) return dateRange;

    const now = new Date();
    const periods = {
      "7d": 7,
      "30d": 30,
      "90d": 90,
    };

    if (selectedPeriod === "custom") {
      return undefined;
    }

    const days = periods[selectedPeriod];
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    return {
      from: from.toISOString().split("T")[0],
      to: now.toISOString().split("T")[0],
    };
  }, [dateRange, selectedPeriod]);

  const { analytics, loading, error, refetch } = useDestinationAnalytics({
    destinationId,
    dateFrom: calculatedDateRange?.from,
    dateTo: calculatedDateRange?.to,
  });

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const deviceData = useMemo(() => {
    if (!analytics?.clicksByDevice) return [];

    const total = Object.values(analytics.clicksByDevice).reduce(
      (sum, count) => (sum as number) + (count as number),
      0
    );
    if (total === 0) return [];

    return [
      {
        label: "Desktop",
        value: analytics.clicksByDevice.DESKTOP,
        percentage:
          (analytics.clicksByDevice.DESKTOP / (total as number)) * 100,
        color: "bg-blue-500",
      },
      {
        label: "Mobile",
        value: analytics.clicksByDevice.PHONE,
        percentage: (analytics.clicksByDevice.PHONE / (total as number)) * 100,
        color: "bg-green-500",
      },
      {
        label: "Tablet",
        value: analytics.clicksByDevice.TABLET,
        percentage: (analytics.clicksByDevice.TABLET / (total as number)) * 100,
        color: "bg-purple-500",
      },
      {
        label: "Other",
        value: analytics.clicksByDevice.OTHER,
        percentage: (analytics.clicksByDevice.OTHER / (total as number)) * 100,
        color: "bg-gray-500",
      },
    ].filter((item) => item.value > 0);
  }, [analytics]);

  const topLocations = useMemo(() => {
    if (!analytics?.clicksByLocation) return [];

    return [...analytics.clicksByLocation]
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5);
  }, [analytics]);

  const hourlyData = useMemo(() => {
    if (!analytics?.clicksByHour) return [];

    // Create array for all 24 hours
    const hours = Array.from({ length: 24 }, (_, i) => {
      const hourData = analytics.clicksByHour.find((h) => h.hour === i);
      return {
        hour: i,
        clicks: hourData?.clicks || 0,
        label: `${i.toString().padStart(2, "0")}:00`,
      };
    });

    return hours;
  }, [analytics]);

  if (loading) {
    return (
      <div
        className={`bg-white rounded-lg shadow border ${
          compact ? "p-4" : "p-6"
        }`}
      >
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="text-center">
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto"></div>
              </div>
            ))}
          </div>
          {showDetails && (
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`bg-white rounded-lg shadow border ${
          compact ? "p-4" : "p-6"
        }`}
      >
        <div className="text-center py-8">
          <svg
            className="w-12 h-12 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Failed to load analytics
          </h3>
          <p className="text-gray-500 mb-4">
            There was an error loading the analytics data.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div
        className={`bg-white rounded-lg shadow border ${
          compact ? "p-4" : "p-6"
        }`}
      >
        <div className="text-center py-8">
          <svg
            className="w-12 h-12 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No analytics data
          </h3>
          <p className="text-gray-500">
            Analytics data will appear here once users start clicking this
            destination.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg shadow border ${compact ? "p-4" : "p-6"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Analytics Overview
        </h3>
        {!dateRange && (
          <div className="flex items-center space-x-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as any)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {formatNumber(analytics.totalClicks)}
          </div>
          <div className="text-sm text-gray-500">Total Clicks</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {formatNumber(analytics.uniqueClicks)}
          </div>
          <div className="text-sm text-gray-500">Unique Clicks</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {analytics.conversionRate
              ? `${analytics.conversionRate.toFixed(1)}%`
              : "N/A"}
          </div>
          <div className="text-sm text-gray-500">Conversion Rate</div>
        </div>
      </div>

      {showDetails && (
        <>
          {/* Device Breakdown */}
          {deviceData.length > 0 && (
            <div className="mb-8">
              <h4 className="text-md font-medium text-gray-900 mb-4">
                Device Breakdown
              </h4>
              <div className="space-y-3">
                {deviceData.map((device) => (
                  <div key={device.label} className="flex items-center">
                    <div className="w-16 text-sm text-gray-600">
                      {device.label}
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-gray-200 rounded-full h-2">
                        <div
                          className={`${device.color} h-2 rounded-full`}
                          style={{ width: `${device.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="w-16 text-sm text-gray-900 text-right">
                      {device.value} ({device.percentage.toFixed(1)}%)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Locations */}
          {topLocations.length > 0 && (
            <div className="mb-8">
              <h4 className="text-md font-medium text-gray-900 mb-4">
                Top Locations
              </h4>
              <div className="space-y-2">
                {topLocations.map((location, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 mr-3">
                        {index + 1}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {location.city ? `${location.city}, ` : ""}
                          {location.region ? `${location.region}, ` : ""}
                          {location.country}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {location.clicks} clicks
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hourly Activity */}
          {hourlyData.some((h) => h.clicks > 0) && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">
                Activity by Hour
              </h4>
              <div className="flex items-end space-x-1 h-32">
                {hourlyData.map((hour) => {
                  const maxClicks = Math.max(
                    ...hourlyData.map((h) => h.clicks)
                  );
                  const height =
                    maxClicks > 0 ? (hour.clicks / maxClicks) * 100 : 0;

                  return (
                    <div
                      key={hour.hour}
                      className="flex-1 flex flex-col items-center"
                      title={`${hour.label}: ${hour.clicks} clicks`}
                    >
                      <div
                        className="w-full bg-blue-500 rounded-t"
                        style={{ height: `${height}%` }}
                      ></div>
                      <div className="text-xs text-gray-500 mt-1 transform -rotate-45 origin-top-left">
                        {hour.hour % 4 === 0 ? hour.label : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Last Activity */}
          {analytics.lastClickAt && (
            <div className="pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Last click: {new Date(analytics.lastClickAt).toLocaleString()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
